/** Connection paths offered by the "Connect an agent" quickstart (`/setup?path=…`). */
export const SETUP_PATHS = ["typescript", "python", "otel", "framework"] as const;
export type SetupPath = (typeof SETUP_PATHS)[number];

export const FRAMEWORKS = ["claude", "openai", "vercel", "langchain"] as const;
export type Framework = (typeof FRAMEWORKS)[number];

/** Which SDK language a path's snippets default to (CLI). */
export type Lang = "ts" | "python";

export interface PathMeta {
  id: SetupPath;
  label: string;
  short: string;
  blurb: string;
  time: string;
  tag?: string;
  /** Monogram shown in the card's icon square when there is no suitable lucide icon. */
  mono?: string;
  lang: Lang;
}

export const PATH_META: Record<SetupPath, PathMeta> = {
  typescript: {
    id: "typescript",
    label: "TypeScript SDK",
    short: "TypeScript",
    blurb: "Node, Bun, Deno, Cloudflare Workers. Version hashing, redaction and tool spans with impact.",
    time: "~5 min",
    tag: "Recommended",
    mono: "TS",
    lang: "ts",
  },
  python: {
    id: "python",
    label: "Python SDK",
    short: "Python",
    blurb: "Python 3.10+, sync and asyncio. No runtime dependencies.",
    time: "~5 min",
    tag: "Recommended",
    mono: "PY",
    lang: "python",
  },
  otel: {
    id: "otel",
    label: "OpenTelemetry",
    short: "OpenTelemetry",
    blurb: "Already instrumented with OTel or OpenInference? Add an exporter or route your Collector.",
    time: "~2 min",
    tag: "No code",
    lang: "ts",
  },
  framework: {
    id: "framework",
    label: "Framework integration",
    short: "Framework",
    blurb: "Claude Agent SDK, OpenAI Agents SDK, Vercel AI SDK, LangGraph / LangChain.",
    time: "~3 min",
    lang: "ts",
  },
};

export const FRAMEWORK_META: Record<Framework, { label: string; langs: Lang[]; note: string }> = {
  claude: {
    label: "Claude Agent SDK",
    langs: ["ts", "python"],
    note: "PostToolUse records tool spans with arguments, results and impact. The hooks never make a permission decision.",
  },
  openai: {
    label: "OpenAI Agents SDK",
    langs: ["ts", "python"],
    note: "A trace processor converts agent, generation, function and handoff spans into traces.insure spans.",
  },
  vercel: {
    label: "Vercel AI SDK",
    langs: ["ts"],
    note: "wrapTools records each tool's execute as a tool span. Model calls are recorded by ti.llm or the AI SDK's OpenTelemetry output.",
  },
  langchain: {
    label: "LangGraph / LangChain",
    langs: ["ts", "python"],
    note: "In Python, the callback handler records chains, LLM calls and tools. In TypeScript, tracedTool records a tool with its action class and impact.",
  },
};

export function isSetupPath(v: unknown): v is SetupPath {
  return typeof v === "string" && (SETUP_PATHS as readonly string[]).includes(v);
}

export function isFramework(v: unknown): v is Framework {
  return typeof v === "string" && (FRAMEWORKS as readonly string[]).includes(v);
}
